import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { IconButton, Switch, Tooltip } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useTablePagination, useAppMeta } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import {
  useLazyGetVendorProfileListQuery,
  useUpdateVendorProfileStatusMutation,
} from "store/services/vendorProfileApi";
import { toast } from "react-toastify";

export const VendorProfileTab = () => {
  const { id: vendor } = useParams();
  const [searchParams] = useSearchParams();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const [updateVendorProfileStatus] = useUpdateVendorProfileStatusMutation();

  const handleStatusChange = (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";

    const isConfirmed = window.confirm(
      `Are you sure you want to change the status to ${newStatus}?`
    );

    if (isConfirmed) {
      updateVendorProfileStatus({ id: row._id, status: newStatus })
        .unwrap()
        .then(() => {
          toast.success(`Vendor profile status updated to ${newStatus}`);
        })
        .catch((error) => {
          const errorMessage = error?.data?.message || "Something went wrong!";
          toast.error(errorMessage);
        });
    }
  };

  const columns = [
    {
      name: "Vendor",
      selector: (row) => row?.vendorName,
      cell: (row) => (
        <div title={row?.vendorName} className="line-clamp-2">
          {row?.vendorName}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Shop Name",
      selector: (row) => row?.shopName,
      cell: (row) => (
        <div title={row?.shopName} className="line-clamp-2">
          {row?.shopName}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => (
        <div title={row?.phone} className="line-clamp-2">
          {row?.phone}
        </div>
      ),
      minWidth: "150px",
    },

    {
      name: "Owner Name",
      selector: (row) => row?.ownerName,
      cell: (row) => (
        <div title={row?.ownerName} className="line-clamp-2">
          {row?.ownerName ? (
            <HighlightedText text={row?.ownerName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "CAN Address",
      selector: (row) => row?.canAddress?.name,
      cell: (row) => (
        <div title={row?.canAddress?.name} className="line-clamp-2">
          {row?.canAddress?.name}
        </div>
      ),
      minWidth: "200px",
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vendor-profile/${row._id}/detail`}>
            <Tooltip title={"View Detail"} arrow>
              <IconButton size="small" aria-label="View Detail">
                <InfoOutlined color="primary" />
              </IconButton>
            </Tooltip>
          </Link>

          <Link to={`/app/crm/vendor-profile/${row._id}/edit`}>
            <Tooltip title={"Edit"} arrow>
              <IconButton size="small" aria-label="Edit">
                <ModeEditOutlineTwoToneIcon />
              </IconButton>
            </Tooltip>
          </Link>

          <Switch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row)}
            color="primary"
            inputProps={{ "aria-label": "Status Toggle" }}
          />
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchVendorProfiles,
    {
      data: vendorProfileList,
      isLoading: loadingVendorProfiles,
      isFetching: fetchingVendorProfiles,
    },
  ] = useLazyGetVendorProfileListQuery();

  React.useEffect(() => {
    fetchVendorProfiles({
      status: activeTab,
      keywords: keyword,
      page: currentPage,
      limit: perPage,
      vendor,
    });
  }, [activeTab, fetchVendorProfiles, keyword, currentPage, perPage, vendor]);

  useAppMeta(`${activeTab} Vendor Profile`);

  return (
    <>
      <TableFilter searchLabel="Name/Account No.">
        <div className="font-medium">{activeTab} Vendor Profiles</div>
      </TableFilter>

      <AppTable
        columns={columns}
        data={vendorProfileList?.data}
        paginationServer
        paginationPerPage={perPage}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        progressPending={loadingVendorProfiles || fetchingVendorProfiles}
        paginationTotalRows={vendorProfileList?.totalItems ?? 0}
      />
    </>
  );
};
